import bugsnagClient from '@/ui/containers/BugsnagClient';
import kebabCase from 'lodash/kebabCase';
import spateLogoFile from '@/assets/images/spate_logo.svg';

const FONT_FAMILIES = ['Montserrat', 'Merriweather', 'Roboto Mono'];

const getLoadedFontFaceRules = () => {
    const rules = [];
    for (const sheet of document.styleSheets) {
        try {
            for (const rule of sheet.cssRules) {
                if (
                    rule instanceof CSSFontFaceRule &&
                    FONT_FAMILIES.some((f) =>
                        rule.style.getPropertyValue('font-family').includes(f),
                    )
                ) {
                    rules.push(rule.cssText);
                }
            }
        } catch (e) {
            // Cross-origin stylesheets throw SecurityError — skip them
        }
    }
    return rules.join('\n');
};

const fetchAndEncodeFont = (url) =>
    fetch(url)
        .then((resp) => resp.blob())
        .then(
            (blob) =>
                new Promise((resolve, reject) => {
                    // https://stackoverflow.com/a/18650249/5551706
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => resolve([url, reader.result]);
                    reader.onerror = (err) => reject(err);
                }),
        );

const embedFontsInCSS = (cssText) => {
    const urls = new Set();
    const urlRegex = /url\(([^)]+)\)/g;
    let match;
    while ((match = urlRegex.exec(cssText)) !== null) {
        const url = match[1].replace(/['"]/g, '');
        if (url.endsWith('.woff2') || url.endsWith('.woff')) {
            urls.add(url);
        }
    }

    return Promise.all(Array.from(urls).map(fetchAndEncodeFont)).then(
        (encodedFonts) =>
            encodedFonts.reduce(
                (acc, [url, encodedFont]) => acc.replaceAll(url, encodedFont),
                cssText,
            ),
    );
};

let fontStyleSheetsProm;

export const fetchFontStyleSheets = () => {
    if (!fontStyleSheetsProm) {
        const cssText = getLoadedFontFaceRules();
        fontStyleSheetsProm = embedFontsInCSS(cssText).then((embedded) => [
            embedded,
        ]);
    }
    return fontStyleSheetsProm;
};

// TODO: Safari could not render the input element when inlining the svg as an image
const UNSUPPORTED_ELEMENTS = ['input'];

const computedStylesToString = (computedStyles) => {
    let styles = '';
    for (let i = 0; i < computedStyles.length; i++) {
        const name = computedStyles.item(i);
        // Skip CSS custom properties (variables) - they won't resolve in SVG foreignObject context
        // and their values are already applied to the actual CSS properties
        if (name.startsWith('--')) {
            continue;
        }
        const value = computedStyles.getPropertyValue(name);

        if (name && value) {
            let priority = computedStyles.getPropertyPriority(name);
            if (priority) priority = `!${priority}`;
            styles += `${name}:${value}${priority};`;
        }
    }
    return styles;
};

const cloneNodeWithComputedStyles = (node, deep = true) => {
    const isElement = node instanceof Element;
    const isClassNameString = typeof node.className === 'string';
    const nodeClassNames = isClassNameString ? node.className.split(' ') : [];
    const isOnlyVisibleInDownload = nodeClassNames.includes(
        'image-download-visible',
    );

    if (isElement && UNSUPPORTED_ELEMENTS.includes(node.tagName.toLowerCase()))
        return null;

    const cloned = node.cloneNode(false);

    if (isElement) {
        cloned.setAttribute(
            'style',
            computedStylesToString(window.getComputedStyle(node)),
        );
        ['id', 'name', 'class'].forEach((attr) => cloned.removeAttribute(attr));
        if (isOnlyVisibleInDownload) cloned.style.display = 'block';

        // TODO: what about other pseudo selectors?
        const beforeStyles = computedStylesToString(
            window.getComputedStyle(node, '::before'),
        );
        const before = document.createElement('span');
        before.setAttribute('style', beforeStyles);
        cloned.prepend(before);
    }

    if (!(deep && node.hasChildNodes())) return cloned;

    for (let i = 0; i < node.childNodes.length; i++) {
        const childNode = cloneNodeWithComputedStyles(node.childNodes[i]);
        if (childNode) cloned.appendChild(childNode);
    }

    return cloned;
};

const downloadImage = (rootDOMNode, opts) => {
    if (!opts.filename && !opts.title) {
        bugsnagClient.notify(
            new Error(
                'downloadImage called without filename or title — falling back to "image.png"',
            ),
        );
    }
    fetchFontStyleSheets()
        .then((embeddedFontStyleSheets) => {
            return embeddedFontStyleSheets.join('');
        })
        .catch((err) => {
            console.error(err);
            bugsnagClient.notify(err);
            return '';
        })
        .then((fontStyleSheetText) => {
            return createImage(rootDOMNode, { ...opts, fontStyleSheetText });
        })
        .then((href) => {
            const downloadLinkEl = document.createElement('a');
            downloadLinkEl.setAttribute(
                'download',
                `${kebabCase(opts.filename || opts.title || 'image')}.png`,
            );
            downloadLinkEl.setAttribute('href', href);
            downloadLinkEl.style.visibility = 'hidden';
            document.body.appendChild(downloadLinkEl);
            downloadLinkEl.click();
            document.body.removeChild(downloadLinkEl);
        })
        .catch((err) => {
            console.error(err);
            bugsnagClient.notify(err);
        });
};

const defaultPadding = new Array(4).fill(0);
const svgNamespaceURI = 'http://www.w3.org/2000/svg';

const createSVGWrapper = ({
    width,
    height,
    padding = defaultPadding,
    title,
    styles,
    elements,
}) => {
    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = padding;

    const titleFontSize = Math.max(
        8 /* min font size */,
        Math.round((34 * width) / 1000),
    );
    const titleLineHeight = titleFontSize * 1.2;
    const totalWidth = width + paddingLeft + paddingRight;
    const totalHeight =
        height + titleLineHeight + 2 * paddingTop + paddingBottom;

    const wrapper = document.createElementNS(svgNamespaceURI, 'svg');
    wrapper.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
    wrapper.setAttribute('width', totalWidth);
    wrapper.setAttribute('height', totalHeight);
    wrapper.setAttribute('style', 'font-family: Montserrat, sans-serif;');

    const defs = document.createElementNS(svgNamespaceURI, 'defs');
    const styleTag = document.createElementNS(svgNamespaceURI, 'style');
    styleTag.textContent = styles;
    defs.appendChild(styleTag);
    wrapper.appendChild(defs);

    const logoRatio = 4.334277777777778;
    const logoWidth = logoRatio * titleFontSize;
    const spateLogo = document.createElementNS(svgNamespaceURI, 'image');
    spateLogo.setAttribute('x', width + paddingLeft - logoWidth);
    spateLogo.setAttribute(
        'y',
        paddingTop + titleLineHeight / 2 - titleFontSize / 2,
    );
    spateLogo.setAttribute('width', logoWidth);
    spateLogo.setAttribute('href', spateLogoFile);

    const chartTitle = document.createElementNS(
        svgNamespaceURI,
        'foreignObject',
    );
    chartTitle.setAttribute('x', paddingLeft);
    chartTitle.setAttribute('y', paddingTop);
    chartTitle.setAttribute('width', width);
    chartTitle.setAttribute('height', titleLineHeight * 2);
    const titleDiv = document.createElement('div');
    titleDiv.setAttribute(
        'style',
        `
        height: 100%;
        font-size: ${titleFontSize}px;
        text-transform: capitalize;
        text-wrap: balance;
        font-weight: 800;
        margin-right: ${logoWidth + paddingRight}px;
        line-height: 1.2;
        overflow: hidden;
    `,
    );
    titleDiv.appendChild(document.createTextNode(title));
    chartTitle.appendChild(titleDiv);

    const foreignObject = document.createElementNS(
        svgNamespaceURI,
        'foreignObject',
    );
    foreignObject.setAttribute('x', paddingLeft);
    foreignObject.setAttribute('y', titleLineHeight + 2 * paddingTop);
    foreignObject.setAttribute('width', width);
    foreignObject.setAttribute('height', height);
    foreignObject.appendChild(elements);

    wrapper.appendChild(chartTitle);
    wrapper.appendChild(spateLogo);
    wrapper.appendChild(foreignObject);

    return wrapper;
};

// technique adapted from https://stackoverflow.com/a/28226736/5551706
const createImage = (rootDOMNode, { title, fontStyleSheetText }) =>
    new Promise((resolve, reject) => {
        const originalWidth = rootDOMNode.offsetWidth;
        // Clear computed styles on the root element to avoid clipping previously hidden content
        const elements = cloneNodeWithComputedStyles(rootDOMNode);
        elements.setAttribute('style', '');
        elements.style.width = `${originalWidth}px`;

        // Render the cloned tree offscreen to get height including previously hidden content
        const offscreenEl = document.createElement('div');
        offscreenEl.style.position = 'absolute';
        offscreenEl.style.left = '-50000px';
        offscreenEl.appendChild(elements);
        document.body.appendChild(offscreenEl);

        const height = elements.offsetHeight;
        const width = originalWidth;

        document.body.removeChild(offscreenEl);

        const padding = new Array(4).fill(16);
        const [paddingTop, paddingRight, paddingBottom, paddingLeft] = padding;
        const totalWidth = width + paddingLeft + paddingRight;
        const totalHeight = height + 60 + paddingTop + paddingBottom;

        // TODO: use something like https://github.com/niklasvh/html2canvas ?
        const wrapper = createSVGWrapper({
            width,
            height,
            title,
            padding,
            elements,
            styles: fontStyleSheetText,
        });

        const serialized = new XMLSerializer().serializeToString(wrapper);
        const image = new Image(totalWidth, totalHeight);
        image.src = `data:image/svg+xml;utf8,${encodeURIComponent(serialized)}`;

        const outputWidth = totalWidth * 4;
        const outputHeight = totalHeight * 4;
        const canvas = document.createElement('canvas');
        canvas.setAttribute('width', outputWidth);
        canvas.setAttribute('height', outputHeight);
        const ctx = canvas.getContext('2d');

        image.addEventListener('error', (err) => reject(err));
        // set timeout to give fonts a chance to load
        image.addEventListener('load', () =>
            setTimeout(() => {
                ctx.drawImage(image, 0, 0, outputWidth, outputHeight);
                resolve(canvas.toDataURL('image/png'));
            }, 500),
        );
    });

export default downloadImage;
